// src/components/ConfirmEmail.js
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../axiosInstance';
import {
  Container,
  Typography,
  CircularProgress,
  Paper,
  Button,
  Stack
} from '@mui/material';

// Bring in your AuthContext so you can call login, signup, etc.
import { AuthContext } from '../context/AuthContext';

// Bring in AuthDrawer to show it like in MealPlanner.js
import AuthDrawer from './AuthDrawer';

const ConfirmEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  // "status" can be: 'loading', 'success', 'failure'
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Confirming your email...');

  // Show/hide the AuthDrawer just like in MealPlanner.js
  const [showDrawer, setShowDrawer] = useState(false);

  const calledOnce = useRef(false);

  // Pull these from your AuthContext so we can pass them to AuthDrawer
  const { login, signup, onRequestPasswordReset, onResendConfirmation } =
    useContext(AuthContext);

  useEffect(() => {
    // If token is missing, fail right away
    if (!token) {
      setStatus('failure');
      setMessage('Invalid confirmation link.');
      return;
    }
    if (calledOnce.current) return;

    calledOnce.current = true;
    // Attempt to confirm the email
    axiosInstance
      .post('/api/auth/confirm-email', { token })
      .then((response) => {
        setStatus('success');
        setMessage(response.data.message || 'Email confirmed successfully!');
      })
      .catch((error) => {
        setStatus('failure');
        const errMsg =
          error.response?.data?.message || 'Error confirming email.';
        setMessage(errMsg);
      });
  }, [token]);

  // Optional: close current tab
  const closeWindow = () => {
    // Will only work if the browser tab was opened via window.open()
    window.close();
  };

  return (
    <Container maxWidth="sm" style={{ marginTop: '2rem' }}>
      <Paper style={{ padding: '2rem' }}>
        {status === 'loading' && (
          <Stack alignItems="center" spacing={2}>
            <Typography>Confirming your email...</Typography>
            <CircularProgress />
          </Stack>
        )}

        {status === 'success' && (
          <Stack alignItems="center" spacing={2}>
            <Typography
              variant="h5"
              sx={{ color: 'success.main', textAlign: 'center' }}
            >
              {message}
            </Typography>
            <Typography textAlign="center">
              Your email has been confirmed. You can close this tab or continue.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={() => navigate('/')}>
                Go to Home
              </Button>
              {/*
                Instead of navigating to /login, we open the AuthDrawer.
                This matches how MealPlanner opens the drawer with setShowDrawer(true).
              */}
              <Button variant="contained" onClick={() => setShowDrawer(true)}>
                Log In
              </Button>
              <Button variant="contained" onClick={closeWindow}>
                Close Tab
              </Button>
            </Stack>
          </Stack>
        )}

        {status === 'failure' && (
          <Stack alignItems="center" spacing={2}>
            <Typography
              variant="h5"
              sx={{ color: 'error.main', textAlign: 'center' }}
            >
              {message}
            </Typography>
            <Typography textAlign="center">
              Please try again or contact support if the issue persists.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={() => navigate('/')}>
                Go to Home
              </Button>
              <Button variant="contained" onClick={closeWindow}>
                Close Tab
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>

      {/*
        We place the AuthDrawer at the bottom so it can open 
        when showDrawer === true. Same approach as in MealPlanner.
      */}
      <AuthDrawer
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onLogin={login}               // Provided by AuthContext
        onSignup={signup}             // Provided by AuthContext
        onRequestPasswordReset={onRequestPasswordReset}
        onResendConfirmation={onResendConfirmation}
      />
    </Container>
  );
};

export default ConfirmEmail;
